import React, { useState } from 'react';
import { connect } from 'react-redux';

// Actions
import {
  addAltToComparisonById,
  addBenchmarkToComparisonById,
  addFundToComparisonById,
  addFundsToComparison,
  addStockToComparisonById,
  addBenchmarksToComparison,
  ADD_FUNDS_TO_COMPARISON,
} from '@/redux/modules/comparison';
import { clearResults } from '@/redux/modules/advanced-search';
import { setFocusedQuickSearchComparison, QUICK_SEARCH_REDUCER_NAME } from '@/redux/modules/quick-search';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';

// Components
import { AdvancedSearchModal, ScreenerModal } from '@/views/components/modals';
import { QuickSearchV2 } from '@/views/components/search/quick-search-v2';
import { withErrorBoundary } from '@/views/components/errors';

// Hooks
import useEnhancedScreener from '@/hooks/useEnhancedScreener';

// Types
import Comparison from '@/types/comparison';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Fund from '@/types/fund';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { ADVANCED_SEARCH_OPTIONS } from '@/utils/constants';
import { formatMessage } from '@/utils/i18n';

// Legacy support
import LegacyComparisonQuickSearch from '@/views/containers/comparison/misc/LegacyComparisonQuickSearch';
import withLegacyVersion from '@/views/components/hocs/withLegacyVersion';

type DispatchProps = {
  addAltToComparisonById: (id: string) => Promise<void>;
  addBenchmarkToComparisonById: (id: string) => Promise<void>;
  addBenchmarksToComparison: (benchmarks: ComparisonBenchmark[]) => Promise<void>;
  addFundToComparisonById: (id: string) => Promise<void>;
  addFundsToComparison: (funds: Fund[]) => Promise<void[]>;
  addStockToComparisonById: (id: string) => Promise<void>;
  clearResults: () => void;
  setFocusedQuickSearchComparison: (focused?: boolean) => void;
};

type StateProps = {
  addingItemsFromScreener: boolean;
  comparison?: Nullable<Comparison>;
  isFocusedQuickSearch?: boolean;
  selectedAdvancedSearchFunds: Fund[];
  selectedAdvancedSearchBenchmarks: ComparisonBenchmark[];
};

type LegacyProps = {
  dark?: boolean;
  isIncomeTab?: boolean;
  user: User;
};

type Props = DispatchProps & StateProps & LegacyProps;

const ComparisonQuickSearch = ({
  addAltToComparisonById,
  addBenchmarkToComparisonById,
  addBenchmarksToComparison,
  addFundToComparisonById,
  addFundsToComparison,
  addStockToComparisonById,
  addingItemsFromScreener,
  clearResults,
  comparison,
  isFocusedQuickSearch,
  selectedAdvancedSearchBenchmarks,
  selectedAdvancedSearchFunds,
  setFocusedQuickSearchComparison,
}: Props) => {
  const { shouldUseEnhancedScreener } = useEnhancedScreener();
  const [showScreenerModal, setShowScreenerModal] = useState<boolean>(false);
  const [screenerOption, setScreenerOption] = useState<ADVANCED_SEARCH_OPTIONS>(ADVANCED_SEARCH_OPTIONS.FUNDS);

  const screenerActionLabel = formatMessage(
    `AdvancedSearchModal.${addingItemsFromScreener ? 'Adding' : screenerOption === ADVANCED_SEARCH_OPTIONS.BENCHMARKS ? 'AddBenchmarks' : 'AddFunds'}`,
  );

  const handleOnCloseScreenerModal = () => {
    if (isFocusedQuickSearch) {
      setFocusedQuickSearchComparison(false);
    }

    setShowScreenerModal(false);
    clearResults();
  };

  const onAdvancedSearchConfirm = () => {
    switch (screenerOption) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS: {
        addFundsToComparison(selectedAdvancedSearchFunds).then(() => handleOnCloseScreenerModal());
        break;
      }
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS: {
        addBenchmarksToComparison(selectedAdvancedSearchBenchmarks).then(() => handleOnCloseScreenerModal());
        break;
      }
    }
  };

  return (
    <>
      <QuickSearchV2
        comparison={comparison}
        onAddAltById={addAltToComparisonById}
        onAddBenchmarkById={addBenchmarkToComparisonById}
        onAddFundById={addFundToComparisonById}
        onAddStockById={addStockToComparisonById}
        onOpenScreenerModal={setShowScreenerModal}
        onSetFocusedItem={setFocusedQuickSearchComparison}
        targetReducer={QUICK_SEARCH_REDUCER_NAME.COMPARISON}
      />

      {showScreenerModal && shouldUseEnhancedScreener && (
        <ScreenerModal
          show={showScreenerModal}
          onCancel={handleOnCloseScreenerModal}
          selectedOption={screenerOption}
          onChangeOption={option => setScreenerOption(option)}
        />
      )}

      {showScreenerModal && !shouldUseEnhancedScreener && (
        <AdvancedSearchModal
          show={showScreenerModal}
          onCancel={() => setShowScreenerModal(false)}
          onConfirm={onAdvancedSearchConfirm}
          actionDisabled={addingItemsFromScreener || (!selectedAdvancedSearchFunds.length && !selectedAdvancedSearchBenchmarks.length)}
          selectedOption={screenerOption}
          actionLabel={screenerActionLabel}
          onChangeOption={option => setScreenerOption(option)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  addingItemsFromScreener: selectIsLoadingEvery(state, [ADD_FUNDS_TO_COMPARISON.type]),
  comparison: state.comparison.activeComparison,
  isFocusedQuickSearch: state.quickSearch.focused,
  selectedAdvancedSearchFunds: state.advancedSearch.fund.selectedFunds,
  selectedAdvancedSearchBenchmarks: state.advancedSearch.benchmark.selectedBenchmarks,
});

const mapDispatchToProps = {
  addAltToComparisonById,
  addBenchmarkToComparisonById,
  addBenchmarksToComparison,
  addFundToComparisonById,
  addFundsToComparison,
  addStockToComparisonById,
  clearResults,
  setFocusedQuickSearchComparison,
};

export default withLegacyVersion(connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(ComparisonQuickSearch)), LegacyComparisonQuickSearch);
